// WebRTC plumbing for remote support (ported from skuru).
// Signaling rides Firestore under remoteSupportSessions/{id}/signals:
//   doc "offer"  { sdp } — written by the customer (sharer)
//   doc "answer" { sdp } — written by the admin (viewer)
//   auto-id docs { role, candidate } — ICE from each side; apply the OTHER's.
// STUN by default; a TURN server can be added without a deploy by writing
// appConfig/webrtc { iceServers: [...] }.

import { addDoc, collection, doc, getDoc, setDoc } from "firebase/firestore";
import { paths } from "@clevis/shared-types";
import { db } from "./firebase";

const DEFAULT_ICE: RTCIceServer[] = [
  { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
];

export async function getRtcConfig(): Promise<RTCConfiguration> {
  try {
    const snap = await getDoc(doc(db, paths.appConfig("webrtc")));
    const servers = snap.get("iceServers") as RTCIceServer[] | undefined;
    if (Array.isArray(servers) && servers.length > 0) {
      return { iceServers: servers };
    }
  } catch {
    /* fall through to STUN */
  }
  return { iceServers: DEFAULT_ICE };
}

export type SignalRole = "customer" | "admin";

export async function writeSdp(
  sessionId: string,
  kind: "offer" | "answer",
  sdp: RTCSessionDescriptionInit,
): Promise<void> {
  await setDoc(doc(db, paths.remoteSupportSignal(sessionId, kind)), {
    type: sdp.type,
    sdp: sdp.sdp ?? "",
    createdAt: Date.now(),
  });
}

export async function writeCandidate(
  sessionId: string,
  role: SignalRole,
  candidate: RTCIceCandidate,
): Promise<void> {
  await addDoc(collection(db, paths.remoteSupportSignals(sessionId)), {
    role,
    candidate: candidate.toJSON(),
    createdAt: Date.now(),
  });
}

// Buffers remote ICE candidates that arrive before setRemoteDescription —
// a routine race with Firestore snapshots. Flush after the description lands.
export class CandidateQueue {
  private queue: RTCIceCandidateInit[] = [];
  private ready = false;

  constructor(private pc: RTCPeerConnection) {}

  async add(candidate: RTCIceCandidateInit): Promise<void> {
    if (this.ready) {
      await this.pc.addIceCandidate(candidate).catch(() => undefined);
    } else {
      this.queue.push(candidate);
    }
  }

  async markReady(): Promise<void> {
    this.ready = true;
    for (const c of this.queue.splice(0)) {
      await this.pc.addIceCandidate(c).catch(() => undefined);
    }
  }
}
